// MonthlyLiaisonReport.jsx - Consumer service overview and referral pipeline report
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const MonthlyLiaisonReport = () => {
  const { user } = useContext(AuthContext);
  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    customer_id: "",
    report_month: new Date().toISOString().slice(0, 7), // YYYY-MM format
  });
  const [customers, setCustomers] = useState([]);

  // Expansion state for sections
  const [expandedSections, setExpandedSections] = useState({
    consumers: true,
    branches: true,
    services: true,
    referrals: true,
    jobCoaches: false,
  });

  // Expansion state for individual branches in the services by branch section
  const [expandedBranches, setExpandedBranches] = useState({});

  // Toggle for showing services within Branch & Counselor view
  const [showServicesInBranch, setShowServicesInBranch] = useState(true);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const customerRes = await secureAxios.get("/api/customers", {
          params: { tenant_id },
        });
        setCustomers(customerRes.data || []);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  // Load report data
  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/monthly-liaison",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            report_month: filters.report_month || undefined,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data);

        await auditApi.logEntry({
          action: "GENERATE_MONTHLY_LIAISON_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading monthly liaison report:", err);
      toast.error("Failed to load monthly liaison report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const toggleBranch = (branch) => {
    setExpandedBranches((prev) => ({
      ...prev,
      [branch]: !prev[branch],
    }));
  };

  const formatNumber = (num, decimals = 0) => {
    return Number(num || 0).toFixed(decimals);
  };

  const formatPercent = (num) => {
    return `${Number(num || 0).toFixed(1)}%`;
  };

  const getStatusColor = (status) => {
    const statusLower = (status || "").toLowerCase();
    if (statusLower === "active") return "success";
    if (statusLower === "inactive" || statusLower === "closed") return "secondary";
    if (statusLower === "pending" || statusLower === "new") return "info";
    if (statusLower === "on hold") return "warning";
    return "primary";
  };

  // Get month name for display
  const getMonthName = (monthStr) => {
    if (!monthStr) return "";
    const [year, month] = monthStr.split("-");
    const date = new Date(year, month - 1);
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-clipboard-data me-2"></i>
            Monthly Liaison Report
          </h2>
          <p className="text-muted mb-0">
            Consumer service overview, service distribution, and referral pipeline
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label">Report Month</label>
              <input
                type="month"
                className="form-control"
                value={filters.report_month}
                onChange={(e) =>
                  setFilters({ ...filters, report_month: e.target.value })
                }
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters({ ...filters, customer_id: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() =>
                  setFilters({
                    customer_id: "",
                    report_month: new Date().toISOString().slice(0, 7),
                  })
                }
              >
                <i className="bi bi-x-circle me-1"></i>
                Reset Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Report Content */}
      {!loading && reportData && (
        <>
          {/* Report Header */}
          <div className="alert alert-primary mb-4">
            <h5 className="alert-heading mb-1">
              <i className="bi bi-calendar3 me-2"></i>
              {getMonthName(filters.report_month)} Summary
            </h5>
            <small>
              Generated on {new Date().toLocaleDateString()} at{" "}
              {new Date().toLocaleTimeString()}
            </small>
          </div>

          {/* Summary Cards */}
          <div className="row g-3 mb-4">
            <div className="col-6 col-md-3">
              <div className="card bg-primary text-white h-100">
                <div className="card-body text-center">
                  <h6 className="card-subtitle mb-2 opacity-75">
                    Total Consumers
                  </h6>
                  <h2 className="card-title mb-0">
                    {reportData.summary?.total_consumers || 0}
                  </h2>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card bg-success text-white h-100">
                <div className="card-body text-center">
                  <h6 className="card-subtitle mb-2 opacity-75">
                    Active This Month
                  </h6>
                  <h2 className="card-title mb-0">
                    {reportData.summary?.active_this_month || 0}
                  </h2>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card bg-info text-white h-100">
                <div className="card-body text-center">
                  <h6 className="card-subtitle mb-2 opacity-75">New Referrals</h6>
                  <h2 className="card-title mb-0">
                    {reportData.summary?.new_referrals || 0}
                  </h2>
                </div>
              </div>
            </div>
            <div className="col-6 col-md-3">
              <div className="card bg-warning text-dark h-100">
                <div className="card-body text-center">
                  <h6 className="card-subtitle mb-2 opacity-75">
                    Services Provided
                  </h6>
                  <h2 className="card-title mb-0">
                    {reportData.summary?.total_services || 0}
                  </h2>
                </div>
              </div>
            </div>
          </div>

          {/* Consumer Overview Section */}
          <div className="card mb-4">
            <div
              className="card-header bg-primary text-white d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => toggleSection("consumers")}
            >
              <span>
                <i className="bi bi-people me-2"></i>
                Consumer Overview
              </span>
              <i
                className={`bi bi-chevron-${
                  expandedSections.consumers ? "up" : "down"
                }`}
              ></i>
            </div>
            {expandedSections.consumers && (
              <div className="card-body">
                <div className="row">
                  {/* Status Breakdown */}
                  <div className="col-md-6 mb-4">
                    <h6 className="text-muted mb-3">
                      <i className="bi bi-pie-chart me-2"></i>
                      Status Breakdown
                    </h6>
                    {reportData.consumersByStatus?.length > 0 ? (
                      <div className="list-group">
                        {reportData.consumersByStatus.map((item, idx) => (
                          <div
                            key={idx}
                            className="list-group-item d-flex justify-content-between align-items-center"
                          >
                            <span>
                              <span
                                className={`badge bg-${getStatusColor(
                                  item.status
                                )} me-2`}
                              >
                                {item.status}
                              </span>
                            </span>
                            <span>
                              <strong>{item.count}</strong>
                              <small className="text-muted ms-2">
                                ({formatPercent(item.percentage)})
                              </small>
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted">No consumer data available</p>
                    )}
                  </div>

                  {/* Customer Breakdown */}
                  <div className="col-md-6 mb-4">
                    <h6 className="text-muted mb-3">
                      <i className="bi bi-building me-2"></i>
                      By Customer
                    </h6>
                    {reportData.consumersByCustomer?.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-sm">
                          <thead className="table-light">
                            <tr>
                              <th>Customer</th>
                              <th className="text-center">Total</th>
                              <th className="text-center">Active</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportData.consumersByCustomer.map((item, idx) => (
                              <tr key={idx}>
                                <td>{item.customer_name || "Unassigned"}</td>
                                <td className="text-center">
                                  <span className="badge bg-secondary">
                                    {item.total_count}
                                  </span>
                                </td>
                                <td className="text-center">
                                  <span className="badge bg-success">
                                    {item.active_count}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted">No customer data available</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Combined Branch & Services Section */}
          <div className="card mb-4">
            <div
              className="card-header bg-info text-white d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => toggleSection("branches")}
            >
              <span>
                <i className="bi bi-diagram-3 me-2"></i>
                By Branch & Counselor
              </span>
              <div className="d-flex align-items-center">
                {expandedSections.branches && (
                  <div 
                    className="btn-group btn-group-sm me-3" 
                    role="group"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <button
                      type="button"
                      className={`btn ${!showServicesInBranch ? 'btn-light' : 'btn-outline-light'}`}
                      onClick={() => setShowServicesInBranch(false)}
                      title="Summary view"
                    >
                      <i className="bi bi-list-ul me-1"></i>
                      Summary
                    </button>
                    <button
                      type="button"
                      className={`btn ${showServicesInBranch ? 'btn-light' : 'btn-outline-light'}`}
                      onClick={() => setShowServicesInBranch(true)}
                      title="Show with services"
                    >
                      <i className="bi bi-clipboard-check me-1"></i>
                      With Services
                    </button>
                  </div>
                )}
                <i
                  className={`bi bi-chevron-${
                    expandedSections.branches ? "up" : "down"
                  }`}
                ></i>
              </div>
            </div>
            {expandedSections.branches && (
              <div className="card-body">
                {!showServicesInBranch ? (
                  /* SUMMARY VIEW - Original side-by-side tables */
                  <div className="row">
                    {/* Branch Breakdown */}
                    <div className="col-md-6 mb-4">
                      <h6 className="text-muted mb-3">
                        <i className="bi bi-geo-alt me-2"></i>
                        By Branch
                      </h6>
                      {reportData.consumersByBranch?.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-sm">
                            <thead className="table-light">
                              <tr>
                                <th>Branch</th>
                                <th className="text-center">Total</th>
                                <th className="text-center">Active</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.consumersByBranch.map((item, idx) => (
                                <tr key={idx}>
                                  <td>
                                    <i className="bi bi-building me-2 text-muted"></i>
                                    {item.branch}
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-secondary">
                                      {item.total_count}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-success">
                                      {item.active_count}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot className="table-secondary">
                              <tr className="fw-bold">
                                <td>Total</td>
                                <td className="text-center">
                                  {reportData.consumersByBranch.reduce(
                                    (sum, b) => sum + (b.total_count || 0),
                                    0
                                  )}
                                </td>
                                <td className="text-center">
                                  {reportData.consumersByBranch.reduce(
                                    (sum, b) => sum + (b.active_count || 0),
                                    0
                                  )}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted">No branch data available</p>
                      )}
                    </div>

                    {/* Counselor Breakdown */}
                    <div className="col-md-6 mb-4">
                      <h6 className="text-muted mb-3">
                        <i className="bi bi-person-badge me-2"></i>
                        By Counselor
                      </h6>
                      {reportData.consumersByCounselor?.length > 0 ? (
                        <div className="table-responsive" style={{ maxHeight: "400px", overflowY: "auto" }}>
                          <table className="table table-sm">
                            <thead className="table-light sticky-top">
                              <tr>
                                <th>Counselor</th>
                                <th>Branch</th>
                                <th className="text-center">Total</th>
                                <th className="text-center">Active</th>
                              </tr>
                            </thead>
                            <tbody>
                              {reportData.consumersByCounselor.map((item, idx) => (
                                <tr key={idx}>
                                  <td>{item.counselor_name || "Unassigned"}</td>
                                  <td>
                                    <small className="text-muted">{item.branch}</small>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-secondary">
                                      {item.total_count}
                                    </span>
                                  </td>
                                  <td className="text-center">
                                    <span className="badge bg-success">
                                      {item.active_count}
                                    </span>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted">No counselor data available</p>
                      )}
                    </div>
                  </div>
                ) : (
                  /* WITH SERVICES VIEW - Expandable branches with services (no counselor level) */
                  <div>
                    {/* Support both old (serviceByBranchCounselor) and new (serviceByBranch) data formats */}
                    {(reportData.serviceByBranch?.length > 0 || reportData.serviceByBranchCounselor?.length > 0) ? (
                      <div className="accordion" id="branchServicesAccordion">
                        {(reportData.serviceByBranch || reportData.serviceByBranchCounselor)?.map((branch, branchIdx) => (
                          <div className="accordion-item" key={branchIdx}>
                            <h2 className="accordion-header">
                              <button
                                className={`accordion-button ${
                                  expandedBranches[branch.branch] ? "" : "collapsed"
                                }`}
                                type="button"
                                onClick={() => toggleBranch(branch.branch)}
                              >
                                <div className="d-flex justify-content-between align-items-center w-100 me-3">
                                  <span>
                                    <i className="bi bi-geo-alt me-2"></i>
                                    <strong>{branch.branch}</strong>
                                  </span>
                                  <span>
                                    <span className="badge bg-primary me-2">
                                      {branch.totals?.consumer_count || 0} Consumers
                                    </span>
                                    <span className="badge bg-success me-2">
                                      {branch.totals?.auth_count || 0} Auths
                                    </span>
                                    <span className="badge bg-info me-2">
                                      {formatNumber(branch.totals?.hours_this_month || 0, 1)} hrs
                                    </span>
                                    {(branch.totals?.miles_this_month > 0) && (
                                      <span className="badge bg-secondary">
                                        {formatNumber(branch.totals?.miles_this_month || 0, 1)} mi
                                      </span>
                                    )}
                                  </span>
                                </div>
                              </button>
                            </h2>
                            <div
                              className={`accordion-collapse collapse ${
                                expandedBranches[branch.branch] ? "show" : ""
                              }`}
                            >
                              <div className="accordion-body">
                                {branch.services?.length > 0 ? (
                                  <div className="table-responsive">
                                    <table className="table table-sm table-hover mb-0">
                                      <thead className="table-light">
                                        <tr>
                                          <th>Service</th>
                                          <th className="text-center">Consumers</th>
                                          <th className="text-center">Auths</th>
                                          <th className="text-end">This Month</th>
                                          <th className="text-end">Total</th>
                                          <th style={{ width: "120px" }}>Distribution</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {branch.services.map((svc, svcIdx) => {
                                          // Check if this is a mileage service based on service_type or is_mileage flag
                                          const isMileageService = svc.is_mileage || svc.service_type === 'mileage';
                                          
                                          // For distribution bar, use appropriate metric
                                          const maxHours = Math.max(
                                            ...branch.services.filter(s => !s.is_mileage && s.service_type !== 'mileage')
                                              .map(s => s.hours_this_month || 0),
                                            1
                                          );
                                          const maxMiles = Math.max(
                                            ...branch.services.filter(s => s.is_mileage || s.service_type === 'mileage')
                                              .map(s => s.miles_this_month || 0),
                                            1
                                          );
                                          
                                          let percentage;
                                          if (isMileageService) {
                                            percentage = maxMiles > 0
                                              ? ((svc.miles_this_month || 0) / maxMiles) * 100
                                              : 0;
                                          } else {
                                            percentage = maxHours > 0
                                              ? ((svc.hours_this_month || 0) / maxHours) * 100
                                              : 0;
                                          }
                                          
                                          return (
                                            <tr key={svcIdx}>
                                              <td>
                                                {svc.service_name}
                                                {svc.flat_rate && (
                                                  <span className="badge bg-info ms-2">Flat Rate</span>
                                                )}
                                                {isMileageService && (
                                                  <span className="badge bg-secondary ms-2">Mileage</span>
                                                )}
                                              </td>
                                              <td className="text-center">
                                                <span className="badge bg-primary">{svc.consumer_count}</span>
                                              </td>
                                              <td className="text-center">
                                                <span className="badge bg-success">{svc.auth_count}</span>
                                              </td>
                                              <td className="text-end">
                                                {isMileageService ? (
                                                  <span>
                                                    {formatNumber(svc.miles_this_month || 0, 1)}
                                                    <small className="text-muted ms-1">mi</small>
                                                  </span>
                                                ) : (
                                                  <span>
                                                    {formatNumber(svc.hours_this_month, 1)}
                                                    <small className="text-muted ms-1">hr</small>
                                                  </span>
                                                )}
                                              </td>
                                              <td className="text-end text-muted">
                                                {isMileageService ? (
                                                  <span>
                                                    {formatNumber(svc.total_miles || 0, 1)}
                                                    <small className="ms-1">mi</small>
                                                  </span>
                                                ) : (
                                                  <span>
                                                    {formatNumber(svc.total_hours, 1)}
                                                    <small className="ms-1">hr</small>
                                                  </span>
                                                )}
                                              </td>
                                              <td>
                                                <div className="progress" style={{ height: "16px" }}>
                                                  <div
                                                    className="progress-bar bg-success"
                                                    role="progressbar"
                                                    style={{ width: `${percentage}%` }}
                                                  ></div>
                                                </div>
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                      <tfoot className="table-secondary">
                                        <tr className="fw-bold">
                                          <td>Total</td>
                                          <td className="text-center text-muted">-</td>
                                          <td className="text-center text-muted">-</td>
                                          <td className="text-end">
                                            {formatNumber(branch.totals?.hours_this_month || branch.services.filter(s => !s.is_mileage && s.service_type !== 'mileage').reduce((sum, s) => sum + (s.hours_this_month || 0), 0), 1)}
                                            <small className="text-muted ms-1">hr</small>
                                          </td>
                                          <td className="text-end">
                                            {formatNumber(branch.totals?.total_hours || branch.services.filter(s => !s.is_mileage && s.service_type !== 'mileage').reduce((sum, s) => sum + (s.total_hours || 0), 0), 1)}
                                            <small className="text-muted ms-1">hr</small>
                                          </td>
                                          <td></td>
                                        </tr>
                                      </tfoot>
                                    </table>
                                  </div>
                                ) : (
                                  <p className="text-muted mb-0 small">
                                    <i className="bi bi-info-circle me-1"></i>
                                    No services logged this month
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center text-muted py-4">
                        <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                        No service data by branch available
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Services Being Received Section (Overall) */}
          <div className="card mb-4">
            <div
              className="card-header bg-success text-white d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => toggleSection("services")}
            >
              <span>
                <i className="bi bi-clipboard-check me-2"></i>
                Services Being Received (Overall)
              </span>
              <i
                className={`bi bi-chevron-${
                  expandedSections.services ? "up" : "down"
                }`}
              ></i>
            </div>
            {expandedSections.services && (
              <div className="card-body">
                {reportData.serviceBreakdown?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Service</th>
                          <th className="text-center">Consumers</th>
                          <th className="text-center">Active Auths</th>
                          <th className="text-end">This Month</th>
                          <th className="text-end">Total</th>
                          <th style={{ width: "150px" }}>Distribution</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.serviceBreakdown.map((svc, idx) => {
                          // Check if this is a mileage service
                          const isMileageService = svc.is_mileage || svc.service_type === 'mileage';
                          
                          // Calculate distribution based on appropriate metric
                          const maxHours = Math.max(
                            ...reportData.serviceBreakdown
                              .filter(s => !s.is_mileage && s.service_type !== 'mileage')
                              .map(s => s.hours_this_month || 0),
                            1
                          );
                          const maxMiles = Math.max(
                            ...reportData.serviceBreakdown
                              .filter(s => s.is_mileage || s.service_type === 'mileage')
                              .map(s => s.miles_this_month || 0),
                            1
                          );
                          
                          let percentage;
                          if (isMileageService) {
                            percentage = maxMiles > 0
                              ? ((svc.miles_this_month || 0) / maxMiles) * 100
                              : 0;
                          } else {
                            percentage = maxHours > 0
                              ? ((svc.hours_this_month || 0) / maxHours) * 100
                              : 0;
                          }
                          
                          return (
                            <tr key={idx}>
                              <td>
                                <strong>{svc.service_name}</strong>
                                {svc.flat_rate && (
                                  <span className="badge bg-info ms-2">
                                    Flat Rate
                                  </span>
                                )}
                                {isMileageService && (
                                  <span className="badge bg-secondary ms-2">
                                    Mileage
                                  </span>
                                )}
                              </td>
                              <td className="text-center">
                                <span className="badge bg-primary">
                                  {svc.consumer_count}
                                </span>
                              </td>
                              <td className="text-center">
                                <span className="badge bg-success">
                                  {svc.auth_count}
                                </span>
                              </td>
                              <td className="text-end">
                                {isMileageService ? (
                                  <span>
                                    {formatNumber(svc.miles_this_month || 0, 1)}
                                    <small className="text-muted ms-1">mi</small>
                                  </span>
                                ) : (
                                  <span>
                                    {formatNumber(svc.hours_this_month, 1)}
                                    <small className="text-muted ms-1">hr</small>
                                  </span>
                                )}
                              </td>
                              <td className="text-end text-muted">
                                {isMileageService ? (
                                  <span>
                                    {formatNumber(svc.total_miles || 0, 1)}
                                    <small className="ms-1">mi</small>
                                  </span>
                                ) : (
                                  <span>
                                    {formatNumber(svc.total_hours, 1)}
                                    <small className="ms-1">hr</small>
                                  </span>
                                )}
                              </td>
                              <td>
                                <div
                                  className="progress"
                                  style={{ height: "20px" }}
                                >
                                  <div
                                    className="progress-bar bg-success"
                                    role="progressbar"
                                    style={{ width: `${percentage}%` }}
                                    aria-valuenow={percentage}
                                    aria-valuemin="0"
                                    aria-valuemax="100"
                                  ></div>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot className="table-secondary">
                        <tr className="fw-bold">
                          <td>Total</td>
                          <td className="text-center text-muted">-</td>
                          <td className="text-center text-muted">-</td>
                          <td className="text-end">
                            <span>
                              {formatNumber(
                                reportData.serviceBreakdown
                                  .filter(s => !s.is_mileage && s.service_type !== 'mileage')
                                  .reduce((sum, s) => sum + (s.hours_this_month || 0), 0),
                                1
                              )}
                              <small className="text-muted ms-1">hr</small>
                            </span>
                          </td>
                          <td className="text-end">
                            <span>
                              {formatNumber(
                                reportData.serviceBreakdown
                                  .filter(s => !s.is_mileage && s.service_type !== 'mileage')
                                  .reduce((sum, s) => sum + (s.total_hours || 0), 0),
                                1
                              )}
                              <small className="ms-1">hr</small>
                            </span>
                          </td>
                          <td></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No service data available for this period
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Referral Pipeline Section */}
          <div className="card mb-4">
            <div
              className="card-header bg-warning text-dark d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => toggleSection("referrals")}
            >
              <span>
                <i className="bi bi-funnel me-2"></i>
                Referral Pipeline
              </span>
              <i
                className={`bi bi-chevron-${
                  expandedSections.referrals ? "up" : "down"
                }`}
              ></i>
            </div>
            {expandedSections.referrals && (
              <div className="card-body">
                {/* Pipeline Summary */}
                <div className="row g-3 mb-4">
                  <div className="col-md-4">
                    <div className="card bg-light h-100">
                      <div className="card-body text-center">
                        <h6 className="text-muted">New This Month</h6>
                        <h2 className="text-info">
                          {reportData.referralPipeline?.new_this_month || 0}
                        </h2>
                        <small className="text-muted">consumers added</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light h-100">
                      <div className="card-body text-center">
                        <h6 className="text-muted">Pending Assessment</h6>
                        <h2 className="text-warning">
                          {reportData.referralPipeline?.pending_assessment || 0}
                        </h2>
                        <small className="text-muted">awaiting intake</small>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="card bg-light h-100">
                      <div className="card-body text-center">
                        <h6 className="text-muted">Started Services</h6>
                        <h2 className="text-success">
                          {reportData.referralPipeline?.started_services || 0}
                        </h2>
                        <small className="text-muted">this month</small>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Recent Referrals Table */}
                {reportData.recentReferrals?.length > 0 && (
                  <>
                    <h6 className="text-muted mb-3">
                      <i className="bi bi-clock-history me-2"></i>
                      Recent Referrals (This Month)
                    </h6>
                    <div className="table-responsive">
                      <table className="table table-sm table-hover">
                        <thead className="table-light">
                          <tr>
                            <th>Consumer</th>
                            <th>Branch</th>
                            <th>Counselor</th>
                            <th>Referred By</th>
                            <th>Date Added</th>
                            <th className="text-center">Status</th>
                            <th className="text-center">Has Auth</th>
                          </tr>
                        </thead>
                        <tbody>
                          {reportData.recentReferrals.map((ref, idx) => (
                            <tr key={idx}>
                              <td className="fw-bold">{ref.consumer_name}</td>
                              <td>
                                <small className="text-muted">{ref.branch || "-"}</small>
                              </td>
                              <td>{ref.counselor_name || "-"}</td>
                              <td>{ref.referred_by || "-"}</td>
                              <td>
                                {new Date(ref.created_at).toLocaleDateString()}
                              </td>
                              <td className="text-center">
                                <span
                                  className={`badge bg-${getStatusColor(
                                    ref.status
                                  )}`}
                                >
                                  {ref.status || "Unknown"}
                                </span>
                              </td>
                              <td className="text-center">
                                {ref.has_authorization ? (
                                  <i className="bi bi-check-circle-fill text-success"></i>
                                ) : (
                                  <i className="bi bi-x-circle text-muted"></i>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Job Coach Assignment Section */}
          <div className="card mb-4">
            <div
              className="card-header bg-secondary text-white d-flex justify-content-between align-items-center"
              style={{ cursor: "pointer" }}
              onClick={() => toggleSection("jobCoaches")}
            >
              <span>
                <i className="bi bi-person-badge me-2"></i>
                Job Coach Assignments
              </span>
              <i
                className={`bi bi-chevron-${
                  expandedSections.jobCoaches ? "up" : "down"
                }`}
              ></i>
            </div>
            {expandedSections.jobCoaches && (
              <div className="card-body">
                {reportData.jobCoachAssignments?.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead className="table-light">
                        <tr>
                          <th>Job Coach</th>
                          <th className="text-center">Assigned Consumers</th>
                          <th className="text-center">Active Auths</th>
                          <th className="text-end">Hours This Month</th>
                          <th className="text-end">Miles This Month</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.jobCoachAssignments.map((coach, idx) => (
                          <tr key={idx}>
                            <td className="fw-bold">{coach.job_coach_name}</td>
                            <td className="text-center">
                              <span className="badge bg-primary">
                                {coach.consumer_count}
                              </span>
                            </td>
                            <td className="text-center">
                              <span className="badge bg-success">
                                {coach.auth_count}
                              </span>
                            </td>
                            <td className="text-end">
                              {formatNumber(coach.hours_this_month, 1)}
                            </td>
                            <td className="text-end">
                              {formatNumber(coach.miles_this_month, 0)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot className="table-secondary">
                        <tr className="fw-bold">
                          <td>Total</td>
                          <td className="text-center">
                            {reportData.jobCoachAssignments.reduce(
                              (sum, c) => sum + (parseInt(c.consumer_count) || 0),
                              0
                            )}
                          </td>
                          <td className="text-center">
                            {reportData.jobCoachAssignments.reduce(
                              (sum, c) => sum + (parseInt(c.auth_count) || 0),
                              0
                            )}
                          </td>
                          <td className="text-end">
                            {formatNumber(
                              reportData.jobCoachAssignments.reduce(
                                (sum, c) =>
                                  sum + (parseFloat(c.hours_this_month) || 0),
                                0
                              ),
                              1
                            )}
                          </td>
                          <td className="text-end">
                            {formatNumber(
                              reportData.jobCoachAssignments.reduce(
                                (sum, c) =>
                                  sum + (parseFloat(c.miles_this_month) || 0),
                                0
                              ),
                              0
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No job coach assignments found
                  </div>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* No Data */}
      {!loading && !reportData && (
        <div className="text-center py-5">
          <i
            className="bi bi-inbox text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Data Available</h5>
          <p className="text-muted">
            Unable to load report data. Please try again.
          </p>
        </div>
      )}
    </div>
  );
};

export default MonthlyLiaisonReport;